import { ofType } from 'redux-observable';
import { from, merge } from 'rxjs';
import { filter, map, mergeMap, pluck, share, withLatestFrom } from 'rxjs/operators';
import { ILeadSource } from '@bridebook/models/source/models/Suppliers/Leads.types';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { getEnquiryDates, getEnquiryFormIntents } from '@bridebook/web/lib/enquiries/selectors';
import { enqueueEnquiry } from '@bridebook/web/lib/enquiries/utils/enqueue-enquiry';
import { verifySpamCheck } from '@bridebook/web/lib/enquiries/utils/spam-check';
import { env } from '@bridebook/web/lib/env';
import {
  OnboardingAction,
  SendEnquiriesPayload,
  sendEnquiriesFailure,
  sendEnquiriesSuccess,
} from '@bridebook/web/lib/onboarding/actions';
import { Action, IApplicationState, IEpic } from '@bridebook/web/lib/types';
import { getCurrentUserId } from '@bridebook/web/lib/users/selectors';
import { getWeddingProfileId } from '@bridebook/web/lib/weddings/selectors';

type InputAction = Required<Action<SendEnquiriesPayload>>;

/**
 * An epic that sends enquiries to suppliers during onboarding.
 *
 * Listens to the `OnboardingAction.SendEnquiries` action and enqueues one enquiry per supplier
 * via the unified `/api/enquiries/enquiry-send` flow — the lead AND TalkJS conversation are created
 * durably in the background by the queue.
 *
 * Filters out spam messages, checks if the user has reached the enquiries limit, and has a valid
 * user ID. Dispatches success/failure actions based on whether any enquiries were enqueued.
 */
export const sendOnboardingEnquiriesEpic: IEpic<InputAction> = (action$, { state$ }) => {
  const enquiries$ = action$.pipe(
    ofType(OnboardingAction.SendEnquiries),
    pluck('payload'),
    withLatestFrom(state$),
    filter(hasNotReachedEnquiriesLimit),
    filter(hasUserId),
    filter(([{ message }, state]) => verifySpamCheck(state, message)),
    mergeMap(([{ suppliers, message, executeRecaptcha }, state]) => {
      const intents = getEnquiryFormIntents(state);
      const weddingId = getWeddingProfileId(state);
      const sendEnquiries = async () => {
        const captchaToken = await executeRecaptcha('sendEnquiry');
        const { id, results } = await enqueueEnquiry({
          suppliers,
          message,
          intents,
          captchaToken,
          source: ILeadSource.Concierge,
          weddingId,
        }).catch(() => ({ id: '', results: [] }));

        const isSuccessful = (supplier: IUISupplier) =>
          results.some((result) => result.supplierId === supplier.id && result.success);

        const successfulEnquiries = suppliers.filter(isSuccessful).map((supplier) => ({
          id,
          enquiryId: id,
          supplier,
          message,
          suppliersCount: suppliers.length,
        }));

        const unsuccessfulEnquiries = suppliers.filter((supplier) => !isSuccessful(supplier));

        return {
          successfulEnquiries,
          unsuccessfulEnquiries,
        };
      };

      return from(sendEnquiries());
    }),
    share(),
  );

  const successfulEnquiries$ = enquiries$.pipe(
    map(({ successfulEnquiries }) => successfulEnquiries),
    filter((data) => !!data.length),
    map(sendEnquiriesSuccess),
  );

  const unsuccessfulEnquiries$ = enquiries$.pipe(
    map(({ unsuccessfulEnquiries }) => unsuccessfulEnquiries),
    filter((data) => !!data.length),
    map(sendEnquiriesFailure),
  );

  return merge(successfulEnquiries$, unsuccessfulEnquiries$);
};

const hasNotReachedEnquiriesLimit = ([{ suppliers }, state]: [
  SendEnquiriesPayload,
  IApplicationState,
]) => {
  const enquiryDates = getEnquiryDates(state);
  const category = suppliers[0].type;
  const enquiriesCount = enquiryDates[category];
  const enquiryLimitReached = enquiriesCount >= Number(env.ENQUIRIES_USER_LIMIT ?? 0);
  return !(enquiriesCount && enquiryLimitReached);
};

const hasUserId = ([, state]: [SendEnquiriesPayload, IApplicationState]) => {
  const userId = getCurrentUserId(state);
  if (!userId) {
    SentryMinimal().captureMessage(`[NEW ONBOARDING]: Attempt to send enquiries without user id.`);
  }
  return !!userId;
};
